"use client";

import { useMemo, useRef, useState } from "react";
import type { RowSelectionState } from "@tanstack/react-table";
import { useReactToPrint } from "react-to-print";
import { LayoutGrid, List, Printer, Plus, Upload } from "lucide-react";
import { useProducts, type ProductListParams } from "@/hooks/api/useProducts";
import { useCategoryTree } from "@/hooks/api/useCategoryTree";
import { useBranches } from "@/hooks/api/useBranches";
import { useBranchesEnabled } from "@/hooks/api/useBranchesEnabled";
import { useUnits } from "@/hooks/api/useUnits";
import { flattenCategories } from "@/lib/categories";
import type { ProductRow } from "@/lib/types/product";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProductTable } from "@/components/products/ProductTable";
import { ProductGrid } from "@/components/products/ProductGrid";
import { ProductFormModal } from "@/components/products/ProductFormModal";
import { BarcodeModal } from "@/components/products/BarcodeModal";
import { BulkImportModal } from "@/components/products/BulkImportModal";
import { ProductLabelSheet } from "@/components/products/ProductLabelSheet";
import { ProductImageDialog } from "@/components/products/ProductImageDialog";
import { useDeleteProduct } from "@/hooks/api/useProductMutations";

export function ProductManagement() {
  const [view, setView] = useState<"list" | "grid">("list");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("");
  const [categoryId, setCategoryId] = useState<number | undefined>();
  const [stockStatus, setStockStatus] = useState<ProductListParams["stock_status"]>("all");
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [formOpen, setFormOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<ProductRow | null>(null);
  const [barcodeProduct, setBarcodeProduct] = useState<ProductRow | null>(null);
  const [imageProduct, setImageProduct] = useState<ProductRow | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const labelRef = useRef<HTMLDivElement>(null);
  const printBatch = useReactToPrint({ contentRef: labelRef });

  const perPage = 15;
  const listParams: ProductListParams = {
    page,
    per_page: perPage,
    search: search || undefined,
    brand: brand || undefined,
    category_id: categoryId,
    stock_status: stockStatus,
    include_inactive: true,
  };

  const q = useProducts(listParams);
  const tree = useCategoryTree();
  const branches = useBranches();
  const { branchesEnabled } = useBranchesEnabled();
  const units = useUnits();
  const del = useDeleteProduct();

  function handleProductDelete(p: ProductRow) {
    if (
      window.confirm(
        `Remove "${p.name}" from POS? Product stays in this catalog (inactive). Turn On in edit to sell again.`,
      )
    ) {
      void del.mutateAsync(p.id);
    }
  }

  const catOptions = useMemo(
    () => flattenCategories(tree.data ?? []),
    [tree.data],
  );

  const branchesList = useMemo(() => branches.data ?? [], [branches.data]);
  const unitsList = useMemo(() => units.data ?? [], [units.data]);

  const rows = useMemo(() => q.data?.data ?? [], [q.data]);
  const pagination = q.data?.pagination;

  const selectedRows = useMemo(() => {
    return rows.filter((r) => rowSelection[String(r.id)]);
  }, [rows, rowSelection]);

  function openCreate() {
    setEditProduct(null);
    setFormOpen(true);
  }

  function openEdit(p: ProductRow) {
    setEditProduct(p);
    setFormOpen(true);
  }

  function toggleGridSelect(id: number, checked: boolean) {
    setRowSelection((prev) => {
      const next = { ...prev };
      if (checked) {
        next[String(id)] = true;
      } else {
        delete next[String(id)];
      }

      return next;
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Products</h1>
          <p className="text-sm text-muted-foreground">Inventory & catalog</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
            <Upload className="mr-1 h-4 w-4" />
            Import
          </Button>
          <Button size="sm" onClick={openCreate}>
            <Plus className="mr-1 h-4 w-4" />
            Add product
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-xl border bg-card p-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="grid flex-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <label className="text-xs text-muted-foreground">Search</label>
            <Input
              placeholder="Name, SKU, barcode…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Brand</label>
            <Input
              placeholder="Brand contains…"
              value={brand}
              onChange={(e) => {
                setBrand(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Category</label>
            <select
              className="flex h-9 w-full rounded-lg border border-input bg-background px-2 text-sm"
              value={categoryId ?? ""}
              onChange={(e) => {
                setCategoryId(e.target.value === "" ? undefined : Number(e.target.value));
                setPage(1);
              }}
            >
              <option value="">All</option>
              {catOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Stock</label>
            <select
              className="flex h-9 w-full rounded-lg border border-input bg-background px-2 text-sm"
              value={stockStatus}
              onChange={(e) => {
                setStockStatus(e.target.value as ProductListParams["stock_status"]);
                setPage(1);
              }}
            >
              <option value="all">All</option>
              <option value="low">Low</option>
              <option value="ok">OK</option>
            </select>
          </div>
        </div>
        <div className="flex rounded-lg border bg-muted p-1">
          <Button
            type="button"
            variant={view === "list" ? "secondary" : "ghost"}
            size="sm"
            className="gap-1"
            onClick={() => setView("list")}
          >
            <List className="h-4 w-4" />
            List
          </Button>
          <Button
            type="button"
            variant={view === "grid" ? "secondary" : "ghost"}
            size="sm"
            className="gap-1"
            onClick={() => setView("grid")}
          >
            <LayoutGrid className="h-4 w-4" />
            Grid
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={selectedRows.length === 0}
          onClick={() => printBatch()}
        >
          <Printer className="mr-1 h-4 w-4" />
          Print labels ({selectedRows.length})
        </Button>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : q.isError ? (
        <p className="text-sm text-destructive">Failed to load products.</p>
      ) : view === "list" ? (
        <ProductTable
          data={rows}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          onEdit={openEdit}
          onBarcode={setBarcodeProduct}
          onImage={setImageProduct}
          onDelete={handleProductDelete}
        />
      ) : (
        <ProductGrid
          data={rows}
          selected={rowSelection}
          onToggle={toggleGridSelect}
          onEdit={openEdit}
          onImage={setImageProduct}
          onDelete={handleProductDelete}
        />
      )}

      {pagination ? (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {pagination.page} / {pagination.last_page} · {pagination.total} items
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pagination.last_page}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <ProductFormModal
        open={formOpen}
        onOpenChange={setFormOpen}
        product={editProduct}
        categories={catOptions}
        units={unitsList}
        branches={branchesList}
        branchesEnabled={branchesEnabled}
      />
      <BarcodeModal
        open={barcodeProduct !== null}
        onOpenChange={(o) => {
          if (!o) {
            setBarcodeProduct(null);
          }
        }}
        product={barcodeProduct}
      />
      <BulkImportModal open={importOpen} onOpenChange={setImportOpen} />
      <ProductImageDialog
        product={imageProduct}
        onOpenChange={(o) => {
          if (!o) {
            setImageProduct(null);
          }
        }}
      />

      <div className="pointer-events-none fixed -left-[9999px] top-0 opacity-0">
        <ProductLabelSheet ref={labelRef} products={selectedRows} />
      </div>
    </div>
  );
}
